import React from 'react';
import { Box, Card, CardContent, TextField, Typography } from '@mui/material';
import type { SeoMeta } from '../../services/api-client';

interface SeoMetaCardProps {
  seo: SeoMeta;
  onChange: (seo: SeoMeta) => void;
  disabled?: boolean;
}

/**
 * SeoMetaCard — reusable SEO metadata fields (title, description, OG image).
 */
export default function SeoMetaCard({ seo, onChange, disabled = false }: SeoMetaCardProps) {
  const titleLen = seo.title?.length ?? 0;
  const descLen = seo.description?.length ?? 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          SEO & Open Graph
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="SEO Title"
          value={seo.title ?? ''}
          onChange={(e) => onChange({ ...seo, title: e.target.value })}
          disabled={disabled}
          helperText={`${titleLen}/60 characters`}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 120 }}
          error={titleLen > 60}
        />

        <TextField
          fullWidth
          size="small"
          label="SEO Description"
          value={seo.description ?? ''}
          onChange={(e) => onChange({ ...seo, description: e.target.value })}
          disabled={disabled}
          helperText={`${descLen}/160 characters`}
          multiline
          rows={3}
          sx={{ mb: 2 }}
          error={descLen > 160}
        />

        <TextField
          fullWidth
          size="small"
          label="OG Image URL"
          value={seo.ogImage ?? ''}
          onChange={(e) => onChange({ ...seo, ogImage: e.target.value })}
          disabled={disabled}
          placeholder="https://..."
        />

        {seo.ogImage && (
          <Box sx={{ mt: 1 }}>
            <Box
              component="img"
              src={seo.ogImage}
              alt="OG Preview"
              sx={{
                width: '100%',
                maxHeight: 120,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
